/* eslint-disable max-len */
import { VoidCallback1 } from '@deqo/foundation';
import Fuse from 'fuse.js';
import React, { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Scrollbars, { positionValues } from 'react-custom-scrollbars';
import { useEvent } from 'react-use';
import styled, { css } from 'styled-components';
import { telemetry } from '../../@vendor/Posthog';
import { toInitials, uuid } from '../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../Legacy';
import { ApiClient } from '../../api/ApiClient';
import { v2 } from '../../api/ApiInterfaceV2';
import { TemplateData, TemplateGroupData } from '../../api/endpoints/templates';
import { CommandDispatcher } from '../../concepts/commands/CommandDispatcher';
import { Icon } from '../../concepts/resources/Icon';
import { useLoader, useSubmitter, useWindowSizeInRem } from '../../platform/Hook';
import { LoadingFrame, LoadingGuard } from '../../ui/LoadingGuard';
import { AppNavigator } from '../AppNavigator';
import { useResolve } from '../Container';
import { BoardThumb } from '../dashboard/elements/BoardThumb';
import { Flexer, LinkButton, Title } from '../dashboard/elements/LayoutElements';
import { Button, Colors, Input, Menu, Prompt } from './../../ui';
import { boardCreate } from './BoardCommands';

const XScrollbars = Scrollbars as any;

type ExtendedTemplateData = TemplateData & {
    group: string,
    initials: string,
};

type ExtendedTemplateGroupData = Omit<TemplateGroupData, 'items'> & { items: ExtendedTemplateData[] };

const extendAndCleanData = (data: TemplateGroupData[]): ExtendedTemplateGroupData[] =>
    data.filter(({ items }) => items.length > 0).map(({ items, ...gp }) => ({
        ...gp,
        items: items.map(tm => ({
            ...tm,
            group: gp.label,
            initials: toInitials(tm.label, true),
        }))
    }));

const MenuItem = styled<any>(Menu.Item)`
    margin-bottom: .25rem !important;
    color: ${Colors.text} !important;
    border-radius: .25rem !important;

    ${p => p.active ? css`
        background: ${Colors.hover} ;
    ` : ''}
`;

const TemplateFrame = styled.div`
    position: relative;
    display: inline-block;
    vertical-align: top;
    margin: 1rem;    
    width: 32.5rem;
    border-radius: .25rem;
    box-shadow: 0 .25rem .5rem 0 rgba(0,0,0,.1);
`;

const TemplateHeading = styled.div`
    font-size: 1rem;
    font-weight: 400;
    padding: .25rem .5rem;
    color: white;
    background: ${Colors.primaryLight};
    text-align: center;
    border-radius: .25rem .25rem 0 0;
`;

const TemplateDetail = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
    padding: 0.25rem;
    color: ${Colors.textLight};
`;

const TemplateOverlay = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
`;

const TemplateDescription = styled.div`
    flex: 1 1 auto;
    padding: 2rem 4rem;
`;

const TemplateActions = styled.div`
    flex: 0 0 auto;
    padding: 2rem;
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    justify-content: center;    
    gap: 2rem;
    background-image: linear-gradient(180deg, transparent, rgba(0,0,0,.15));
`;

const TemplateBorder = styled.div<{ bg?: string }>`
    position: relative;
    width: 32.5rem;
    height: 24rem;
    border: solid 0.1875rem #d2e1ec;
    border-radius: 0 0 .25rem .25rem;
    border-top: none;
    overflow: hidden;
    background: ${p => p.bg ?? 'none'};
    background-size: cover;
    background-position: center;

    img {   
        position: relative;
        width: 100%;
        height: 100%;         
        object-fit: cover;
    }

    ${TemplateOverlay} {
        opacity: 0; 
    }

    &:hover {
        ${TemplateOverlay} {
            opacity: 1;
        }
        img {
            filter: blur(1rem);
            opacity: .4;
        }
    }
`;

const GroupTitle = styled(Title)`
    color: ${Colors.text};
    font-size: 2.5rem;
    margin: 1rem;
`;

const SuggestLink = styled.a`
    display: block;
    margin-left: auto;
    color: ${Colors.primary};
    padding: .5rem .25rem;
    font-weight: 400;
    font-size: 0.9375rem;

    svg {
        margin-right: .25rem;   
    }
    
    span:hover {
        text-decoration: underline;
    }
`;

const TypeAnywhere: LFC<{ target: string | RefObject<HTMLInputElement> }> = ({ target, children }) => {

    useEvent('keydown', (e: KeyboardEvent) => {
        const t = (typeof (target) === 'string' ? document.querySelector(target) : target.current) as any;
        if (!!t && e.target !== t) {
            t.focus();
        }
    });

    return <>{children}</>;
};

const TemplateComplexity = ({ value }: { value: number }) => {
    return <>
        <Icon size={1} src={`deqo://icons/i8/star${value >= 1 ? '-filled' : ''}`} />
        <Icon size={1} src={`deqo://icons/i8/star${value >= 2 ? '-filled' : ''}`} />
        <Icon size={1} src={`deqo://icons/i8/star${value >= 3 ? '-filled' : ''}`} />
    </>;
};

type TemplateItemProps = { tm: ExtendedTemplateData, onSelect: VoidCallback1<ExtendedTemplateData> };
const TemplateItem = React.memo<TemplateItemProps>(({ tm, onSelect }) =>
    <TemplateFrame key={`${tm.group}:${tm.id}`}>
        <TemplateHeading>{tm.label}</TemplateHeading>
        <TemplateBorder>
            <BoardThumb info={{ id: tm.source, thumbUrl: tm.thumbUrl }} />
            <TemplateOverlay>
                <TemplateDescription>{tm.description}</TemplateDescription>
                <TemplateActions>
                    <LinkButton target="_blank" to={`${tm.id}`}>Preview Template</LinkButton>
                    <Button variant="primary" onAction={() => onSelect(tm)}>Use Template</Button>
                </TemplateActions>
            </TemplateOverlay>
        </TemplateBorder>
        <Flexer justify="space-between" flex="row">
            {tm.time && <TemplateDetail><Icon size={1.5} src={'deqo://icons/i8/clock'} />{tm.time}</TemplateDetail>}
            {tm.people && <TemplateDetail><Icon size={1.5} src={'deqo://icons/i8/team'} />{tm.people}</TemplateDetail>}
            {tm.complexity && <TemplateDetail><TemplateComplexity value={tm.complexity} /></TemplateDetail>}
        </Flexer>
    </TemplateFrame>
);

type TemplateGroupMenuProps = {
    groups: ExtendedTemplateGroupData[],
    onSelect?: VoidCallback1<ExtendedTemplateGroupData>,
    active?: ExtendedTemplateGroupData,
};
const TemplateGroupMenu = React.memo<TemplateGroupMenuProps>(({ groups, onSelect, active }) =>
    <Menu>
        {groups.map(gp =>
            <MenuItem key={gp.id} active={active === gp} onClick={() => onSelect(gp)}>
                <Icon size={2} src={gp.icon ?? 'deqo://icons/dash/folder-grid-icon'} />
                {gp.label}
            </MenuItem>
        )}
    </Menu>
);

type TemplateListSelectorProps = {
    maxWidth: number,
    groups: ExtendedTemplateGroupData[],
    initialGroup: ExtendedTemplateGroupData,
    onSelect: VoidCallback1<ExtendedTemplateData>,
};

const TemplateListSelector = React.memo<TemplateListSelectorProps>(({ maxWidth, groups, initialGroup, onSelect }) => {

    const [active, setActive] = useState(groups[0]);

    const scroller = useRef<any>();
    const onScrollTo = useCallback((gp: ExtendedTemplateGroupData) => {
        const a = document.querySelector(`#anchor-${gp.id}`) as HTMLElement;
        scroller.current.scrollTop(a.offsetTop);
    }, [scroller]);

    const onScroll = useCallback((values: positionValues) => {
        const a = groups.find((gp, i) => {
            const current = document.querySelector(`#anchor-${gp.id}`) as HTMLElement;
            const next = document.querySelector(`#anchor-${groups[i + 1]?.id}`) as HTMLElement;

            // Is current scroll at top?
            if (values.scrollTop >= 0 && values.scrollTop < current?.offsetTop) return groups[0] === gp;

            // Is current scroll at bottom?
            if (values.scrollTop + 1 >= values.scrollHeight - values.clientHeight) return groups[groups.length - 1] === gp;

            // Is offsetTop less than current scroll but greater than next group's offsetTop?
            if (!next) return true;

            const { offsetTop } = current;
            const { offsetTop: nextOffsetTop } = next;

            return values.scrollTop >= offsetTop && values.scrollTop < nextOffsetTop;
        });
        if (a) {
            setActive(a);
        }
    }, [groups]);

    useEffect(() => {
        const initial = document.querySelector(`#anchor-${initialGroup.id}`) as HTMLElement;
        scroller.current.scrollTop(initial.offsetTop);
        setActive(initialGroup);
    }, [initialGroup]);

    return <Flexer relative grow>
        <Flexer relative maxWidth={maxWidth} margin="0 auto">
            <Flexer absolute margin="0.75 -1 0 0" flex="column" shrink zIndex="10">
                <TemplateGroupMenu groups={groups} onSelect={onScrollTo} active={active} />
            </Flexer>
        </Flexer>
        <Flexer absolute inset="0">
            <XScrollbars ref={scroller} onScrollFrame={onScroll}>
                <Flexer maxWidth={maxWidth} margin="0 auto" padding="0 0 0 17">
                    {groups.map(gp =>
                        gp.items[0] && <Flexer key={gp.id}>
                            <div id={`anchor-${gp.id}`} />
                            <Flexer justify="space-between" flex="row">
                                <GroupTitle>{gp.label}</GroupTitle>
                            </Flexer>
                            <Flexer wrap="wrap" flex="row">
                                {gp.items.map(tm =>
                                    <TemplateItem key={`${tm.group}:${tm.id}`} tm={tm} onSelect={onSelect} />
                                )}
                            </Flexer>
                        </Flexer>
                    )}
                </Flexer>
            </XScrollbars>
        </Flexer>
    </Flexer>;
});

type FromSearchTempateAction =
    { type: 'switch', group: ExtendedTemplateGroupData }
    | { type: 'select', template: ExtendedTemplateData };

type FromSearchTemplateItemSelector = {
    maxWidth: number,
    groups: ExtendedTemplateGroupData[],
    search: string,
    items: ExtendedTemplateData[],
    onAction(action: FromSearchTempateAction): void,
};
const FromSearchTemplateItemSelector = React.memo<FromSearchTemplateItemSelector>(({ maxWidth, groups, search, items, onAction }) => {

    const fuse = useMemo(() => new Fuse(items, {
        threshold: .5,
        shouldSort: true,
        keys: [
            { name: 'label', weight: 1 },
            { name: 'initials', weight: .5 },
            // { name: 'group', weight: .1 },
        ]
    }), []);

    const results = fuse.search(search);

    return <Flexer relative grow>
        <Flexer relative maxWidth={maxWidth} margin="0 auto">
            <Flexer absolute margin="0.75 -1 0 0" flex="column" shrink zIndex="10">
                <TemplateGroupMenu groups={groups} onSelect={group => onAction({ type: 'switch', group })} />
            </Flexer>
        </Flexer>
        <Flexer absolute inset="0">
            <XScrollbars>
                <Flexer maxWidth={maxWidth} margin="0 auto" padding="0 0 0 17">
                    <GroupTitle>{results.length} Results</GroupTitle>
                    {results.map(({ item: tm }) =>
                        <TemplateItem key={`${tm.group}:${tm.id}`} tm={tm} onSelect={() => onAction({ type: 'select', template: tm })} />
                    )}
                </Flexer>
            </XScrollbars>
        </Flexer>
    </Flexer>;

    // return <Flexer gap="2" flex="row" grow>
    //     <Flexer margin="0.75 -1 0 0" flex="column" shrink>
    //         <TemplateGroupMenu groups={groups} onSelect={group => onAction({ type: 'switch', group })} />
    //     </Flexer>
    //     <Flexer relative grow>
    //         <Flexer absolute inset="0">
    //             <XScrollbars>

    //             </XScrollbars>
    //         </Flexer>
    //     </Flexer>
    // </Flexer>;
});

type TemplateSelector = { groups: ExtendedTemplateGroupData[], onSelect: VoidCallback1<ExtendedTemplateData> };
const TemplateSelector = React.memo<TemplateSelector>(({ groups, onSelect }) => {

    const searchId = useMemo(() => uuid().slice(0, 6).padStart(7, '_'), []);
    const [search, setSearch] = useState('');
    const [initialGroup, setInitialGroup] = useState(groups[0]);
    const allItems = useMemo(() => groups.flatMap(x => x.items), [groups]);

    const ws = useWindowSizeInRem();
    const maxWidth = ws.width >= 120.5 ? 120.5 : 86;

    useEffect(() => {
        if (search !== '') {
            telemetry.emitDebounced('template.search', { search }, 500);
        }
    }, [search]);

    const onSearchAction = useCallback((a: FromSearchTempateAction) => {
        if (a.type === 'select') {
            onSelect(a.template);
        }
        else {
            setSearch('');
            setInitialGroup(a.group);
        }
    }, [onSelect]);

    const onPromptAction = useCallback(() => {
        if (search) {
            setSearch('');
            setInitialGroup(null);
        }
        else {
            onSelect(null);
        }
    }, [search, onSelect]);

    const view = !!search ? 'search' : 'menu';

    return <Prompt ignoreEnter ignoreMouse onAction={onPromptAction}>
        <TypeAnywhere target={`#${searchId}`}>
            <Flexer flex="column" align="stretch" grow padding="0 2">
                {/* Header */}
                <Flexer flex="row" justify="center" shrink>
                    <Flexer flex="column" grow maxWidth={maxWidth} padding="2 0">
                        <Input id={searchId} autoFocus placeholder="Search Templates" value={search} onChange={setSearch} />
                        <SuggestLink target="_blank" href="https://docs.google.com/forms/d/e/1FAIpQLScJxBLmhd_TDbBAIluMJyDeE2Nih7OVnnbgff_E3aCSrrLcmA/viewform?usp=sf_link">
                            <Icon size="1.25" src="deqo://icons/hero/external-link" />
                            <span>Suggest a template</span>
                        </SuggestLink>
                    </Flexer>
                </Flexer>
                {/* Body */}
                <Flexer flex="column" grow>
                    {view === 'menu' && <TemplateListSelector {...{ maxWidth, groups, initialGroup, onSelect }} />}
                    {view === 'search' && <FromSearchTemplateItemSelector {...{ maxWidth, groups, search }} items={allItems} onAction={onSearchAction} />}
                </Flexer>
            </Flexer>
        </TypeAnywhere>
    </Prompt>;
});


export const BoardBuilderView = ({ onComplete }: { onComplete }) => {

    const api = useResolve(ApiClient);
    const dispatch = useResolve(CommandDispatcher);
    const nav = useResolve(AppNavigator);

    const [loading, data] = useLoader(async () => extendAndCleanData(await api.exec(v2.templates.list())), []);
    const [onSelect, working] = useSubmitter(async (tmpl: ExtendedTemplateData) => {
        if (tmpl) {
            const boardId = await dispatch(boardCreate, [tmpl.source]);
            nav.go(`~/${boardId as string}?action=template`);
            onComplete();
        }
        else {
            onComplete();
        }
    }, [onComplete]);

    if (loading) {
        return <LoadingFrame />;
    }

    return <LoadingGuard loading={working}>
        <TemplateSelector {...{ groups: data, onSelect }} />
    </LoadingGuard>;
};
